import { GetSchedulerVersionsError } from '@automata/wfb-api';
import { useCallback, useEffect, useState } from 'react';

import { useSchedulerVersions } from '../useSchedulerVersions';

export interface SchedulerVersionResolutionPrompt {
  currentVersion: string;
  defaultSelectedVersion: string;
  onConfirm: (selectedVersion: string) => void;
}

export const useInitializeSchedulerVersion = (
  isFetchingWorkflow: boolean,
  workflowMaestroVersion?: string,
  onVersionDetermined?: (version: string) => void
): {
  schedulerVersionResolutionPrompt: SchedulerVersionResolutionPrompt | null;
  isLoading: boolean;
  error: GetSchedulerVersionsError | Error | null;
} => {
  const [hasCheckedVersion, setHasCheckedVersion] = useState(false);
  const [
    schedulerVersionResolutionPrompt,
    setSchedulerVersionResolutionPrompt,
  ] = useState<SchedulerVersionResolutionPrompt | null>(null);
  const [initializationError, setInitializationError] = useState<
    GetSchedulerVersionsError | Error | null
  >(null);

  const { availableVersions, latestVersion, isLoading, error } =
    useSchedulerVersions();

  const handleModalConfirm = useCallback(
    (selectedVersion: string) => {
      onVersionDetermined?.(selectedVersion);
      setSchedulerVersionResolutionPrompt(null);
      setHasCheckedVersion(true);
    },
    [onVersionDetermined]
  );

  const handleVersionUpdate = useCallback(
    (newVersion: string, requiresConfirmation = false) => {
      if (requiresConfirmation) {
        setSchedulerVersionResolutionPrompt({
          currentVersion: workflowMaestroVersion || '',
          defaultSelectedVersion: newVersion,
          onConfirm: handleModalConfirm,
        });
      } else {
        onVersionDetermined?.(newVersion);
        setHasCheckedVersion(true);
        setSchedulerVersionResolutionPrompt(null);
      }
    },
    [workflowMaestroVersion, handleModalConfirm, onVersionDetermined]
  );

  useEffect(() => {
    if (hasCheckedVersion || isLoading || isFetchingWorkflow) return;

    if (error) {
      setInitializationError(error);
      return;
    }

    if (!latestVersion) {
      setInitializationError(new Error('failed to get the latest version'));
      return;
    }

    // No version use latest
    if (!workflowMaestroVersion) {
      handleVersionUpdate(latestVersion);
      return;
    }

    // If workflow version is supported, use it
    if (availableVersions.includes(workflowMaestroVersion)) {
      handleVersionUpdate(workflowMaestroVersion);
      return;
    }

    handleVersionUpdate(latestVersion, true);
  }, [
    isFetchingWorkflow,
    hasCheckedVersion,
    workflowMaestroVersion,
    isLoading,
    error,
    handleVersionUpdate,
    availableVersions,
    latestVersion,
  ]);

  return {
    schedulerVersionResolutionPrompt,
    isLoading,
    error: initializationError,
  };
};
